import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Sentry } from './lib/sentry';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ButterProvider } from './contexts/ButterContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AdminRoute } from './components/admin';
import { Landing } from './pages/Landing';
import { Register } from './pages/Register';
import { Login } from './pages/Login';
import { ForgotPassword } from './pages/ForgotPassword';
import { ProfileSetup } from './pages/ProfileSetup';
import { Dashboard } from './pages/Dashboard';
import { Profile } from './pages/Profile';
import Leads from './pages/Leads';
import LeadDetail from './pages/LeadDetail';
import { Forms } from './pages/Forms';
import { FormEditor } from './pages/FormEditor';
import { PublicForm } from './pages/PublicForm';
import { SitePages } from './pages/SitePages';
import { SitePageEditor } from './pages/SitePageEditor';
import { PublicSitePage } from './pages/PublicSitePage';
import Website from './pages/Website';
import SocialMedia from './pages/SocialMedia';
import Einstellungen from './pages/Einstellungen';
import { Media } from './pages/Media';
import { Analytics } from './pages/Analytics';
import { Todos } from './pages/Todos';
import { Products } from './pages/Products';
import { ProductDetail } from './pages/ProductDetail';
import { Calendar } from './pages/Calendar';
import { Impressum } from './pages/Impressum';
import { Datenschutz } from './pages/Datenschutz';
import { AGB } from './pages/AGB';
import {
  AdminDashboard,
  AdminUsers,
  AdminUserDetailPage,
  AdminProducts,
  AdminProductEditor,
  AdminMedia,
  AdminAI,
  AdminFeedback,
} from './pages/admin';

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, isProfileComplete } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (user) {
    if (!isProfileComplete) {
      return <Navigate to="/profile-setup" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <BrowserRouter>
      <ButterProvider>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/en" element={<Landing lang="en" />} />
          <Route path="/fr" element={<Landing lang="fr" />} />
          <Route path="/es" element={<Landing lang="es" />} />
          <Route path="/tr" element={<Landing lang="tr" />} />
          <Route path="/ja" element={<Landing lang="ja" />} />
          <Route path="/hi" element={<Landing lang="hi" />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/datenschutz" element={<Datenschutz />} />
          <Route path="/agb" element={<AGB />} />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            }
          />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPassword />
              </PublicRoute>
            }
          />
          <Route
            path="/profile-setup"
            element={
              <ProtectedRoute requireCompleteProfile={false} showSidebar={false}>
                <ProfileSetup />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/todos"
            element={
              <ProtectedRoute>
                <Todos />
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar"
            element={
              <ProtectedRoute>
                <Calendar />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leads"
            element={
              <ProtectedRoute>
                <Leads />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leads/:id"
            element={
              <ProtectedRoute>
                <LeadDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/products"
            element={
              <ProtectedRoute>
                <Products />
              </ProtectedRoute>
            }
          />
          <Route
            path="/products/:slug"
            element={
              <ProtectedRoute>
                <ProductDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/forms"
            element={
              <ProtectedRoute>
                <Forms />
              </ProtectedRoute>
            }
          />
          <Route
            path="/forms/:id"
            element={
              <ProtectedRoute>
                <FormEditor />
              </ProtectedRoute>
            }
          />
          <Route path="/f/:slug" element={<PublicForm />} />
          <Route
            path="/site/pages"
            element={
              <ProtectedRoute>
                <SitePages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/site/page/:type"
            element={
              <ProtectedRoute>
                <SitePageEditor />
              </ProtectedRoute>
            }
          />
          <Route path="/u/:slug/:pageType" element={<PublicSitePage />} />
          <Route
            path="/website"
            element={
              <ProtectedRoute>
                <Website />
              </ProtectedRoute>
            }
          />
          <Route
            path="/social-media"
            element={
              <ProtectedRoute>
                <SocialMedia />
              </ProtectedRoute>
            }
          />
          <Route
            path="/einstellungen"
            element={
              <ProtectedRoute>
                <Einstellungen />
              </ProtectedRoute>
            }
          />
          <Route
            path="/media"
            element={
              <ProtectedRoute>
                <Media />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <Analytics />
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <AdminRoute>
                <AdminUsers />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/users/:id"
            element={
              <AdminRoute>
                <AdminUserDetailPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/products"
            element={
              <AdminRoute>
                <AdminProducts />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/products/:slug"
            element={
              <AdminRoute>
                <AdminProductEditor />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/media"
            element={
              <AdminRoute>
                <AdminMedia />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/ai"
            element={
              <AdminRoute>
                <AdminAI />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/feedback"
            element={
              <AdminRoute>
                <AdminFeedback />
              </AdminRoute>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
      </ButterProvider>
    </BrowserRouter>
  );
}

export default Sentry.withErrorBoundary(App, {
  fallback: (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Ein Fehler ist aufgetreten</h1>
        <p className="text-gray-600 mb-4">Bitte lade die Seite neu.</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90"
        >
          Seite neu laden
        </button>
      </div>
    </div>
  ),
});
